/* 周泰（字幼平）
 * v0.5.0 重製：不耗資源（res: 'none'），技能 v1 照 LoL 卡特蓮娜；天賦「熊虎」擊殺或助攻刷新 Q、W、E。
 * 史實：宣城之役捨身護孫權，「身被十二創」；孫權說他「為孤兄弟戰如熊虎，不惜軀命」（《三國志·吳書·周泰傳》注引《江表傳》）。 */
import { SG, gd, spMul } from '../../core/kit.ts';
import type { Unit } from '../types.ts';
import { defineHero, hook, U, S } from '../define.ts';

const ID = 'zhoutai';
// 十二創引導中會被打斷的狀態
const ccd = (u: Unit) => { const s = u.st; return s.stun > 0 || s.knockup > 0 || s.freeze > 0 || s.down > 0 || s.silence > 0 || s.fear > 0 || s.charm > 0 || s.taunt > 0 || u.grabbedBy || u.thrown; };
function endLotus(u: Unit) { u.ztLotus = null; u.slashing = false; }

export default defineHero({
  id: ID, name: '周泰', sub: '字幼平', faction: 'wu', role: '刺客', weapon: 'sword', res: 'none',
  look: { head: 'topknot', eye: 'fierce', beard: 'mustache', skin: '#c8905e', bulk: 1.15, fx: '#ff6a5a', c: { armor: '#8f2a24' } },
  passive: ['熊虎', '參與擊殺敵將（擊殺或助攻）時，飛刃、刀舞、疾影的冷卻立即歸零。'],
  skills: [
    ['飛刃', 'bolt', '對一名敵人擲出飛刀（必中），造成 {80/135/190/245} 物理傷害並標記 4 秒：周泰普攻被標記的敵人，傷害 +{20/30/40/50}%。',
      { x: { k: 'ztKnife', dmg: [80, 135, 190, 245], amp: [20, 30, 40, 50], dur: 4, speed: 1500, at: 0.3 }, aim: U(600), shape: 'knife', cd: [10, 9, 8, 7], el: null }],
    ['刀舞', 'spin', '揮刀繞身一圈，立即對周圍 260 的敵人造成 {90/145/200/255} 物理傷害。不必停下：邊走邊放、不打斷普攻。',
      { x: { k: 'ztWhirl', dmg: [90, 145, 200, 255], r: 260 }, aim: S(260), free: true, cd: [9, 8, 7, 6], el: null }],
    ['疾影', 'blink', '瞬移到敵人身邊並立刻攻擊一次，暈眩 0.5 秒。',
      { x: { k: 'blinkhit', stun: 0.5 }, aim: U(480), wardHop: true, cd: [18, 14, 10, 6], el: null }],
    ['十二創', 'spin', '原地旋轉引導 2.5 秒，每秒 6 次對 450 內最近的最多 3 名敵將擲刀，每刀 {30/42/54} 物理傷害。被控制就中斷；自己移動也會提前結束。',
      { x: { k: 'ztLotus', dmg: [30, 42, 54], r: 450, n: 3, dur: 2.5, at: 0.05 }, aim: S(450), cd: [80, 70, 60], el: null, shout: '身被十二創，猶死戰不退！' }],
  ],
  kinds: {
    // 飛刃：點選一名敵人，飛刀追著他飛（必中），標記；普攻被標記的人增傷（damage.before 掛勾）
    ztKnife(u, a, X) {
      const t = this.xPick(u, a.pt, 150);
      if (!t || t.structure) { this.xRefund(u, a); return; }
      const sp = spMul(u);
      this.shootProj(u, { homing: t, speed: X.speed, shape: 'knife', col: u.spec.fx, big: true,
        onHit: (o: Unit) => {
          if (!o.alive) return;
          this.damage(u, o, X.dmg * sp, { stagger: 0.2, sk: true });
          if (o.alive) o.ztMark = { by: u.id, until: this.t + X.dur, amp: X.amp / 100 };
          this.fx({ k: 'spark', x: o.x, y: o.y - 30, col: u.spec.fx });
        } });
    },
    // 刀舞：免動作技（free），以自己為中心立即打一圈，不停下腳步
    ztWhirl(u, a, X) {
      for (const t of this.enemiesNear(u, u.x, u.y, X.r)) if (!t.structure) this.xApply(u, t, X, a, 1);
      this.fx({ k: 'bladering', x: u.x, y: u.y, r: X.r, col: u.spec.fx });
    },
    // 十二創（死亡蓮華）：原地引導，每 1/6 秒對最近的最多 3 名敵將擲刀；被控制、自己移動就結束
    ztLotus(u, a, X) {
      const sp = spMul(u), L = { until: this.t + X.dur, x: u.x, y: u.y };
      u.ztLotus = L; u.slashing = true; // slashing：引導中不接受操作（sim.ts control）
      if (u.input) { u.input.moveTo = null; u.input.skill = null; }
      const tick = () => {
        if (u.ztLotus !== L) return;
        if (!u.alive || ccd(u) || this.t >= L.until || gd(u.x, u.y, L.x, L.y) > 30) { endLotus(u); return; }
        if (u.human && u.input && (u.input.moveTo || u.input.skill || u.input.amove)) { endLotus(u); return; } // 自己移動或放別的技能：提前結束
        const ts = this.units.filter((t) => t.kind === 'hero' && this.isEnemy(u, t) && t.alive && gd(u.x, u.y, t.x, t.y) <= X.r)
          .sort((p, q) => gd(u.x, u.y, p.x, p.y) - gd(u.x, u.y, q.x, q.y)).slice(0, X.n);
        for (const t of ts) this.shootProj(u, { homing: t, speed: 1700, shape: 'knife', col: u.spec.fx, onHit: (o: Unit) => { if (o.alive) this.damage(u, o, X.dmg * sp, { stagger: 0.05, sk: true }); } });
        if (ts.length) this.fx({ k: 'whirl', x: u.x, y: u.y, col: u.spec.fx });
        this.later(1 / 6, tick);
      };
      tick();
    },
  },
  hooks: [
    // 飛刃的標記：周泰普攻被標記的人增傷
    hook('damage.before', 45, function (c) {
      const m = c.t.ztMark;
      if (m && m.by === c.own.id && m.until > this.t && c.o && c.o.basic) c.a *= 1 + m.amp;
    }),
    // 熊虎：記下 10 秒內打過哪些敵將（助攻判定），參與擊殺就刷新 Q、W、E
    hook('damage.after', 30, function (c, lost) {
      const u = c.own;
      if (u.spec && u.spec.id === ID && c.t.kind === 'hero' && lost > 0) (u.ztHit || (u.ztHit = {}))[c.t.id] = this.t;
    }),
    hook('kill.after', 30, function (src, t, was) {
      if (!was) return;
      const killer = src && (src.owner || src);
      for (const u of this.heroes(1 - t.team)) {
        if (!u.spec || u.spec.id !== ID || !u.alive) continue;
        if (killer !== u && !(u.ztHit && u.ztHit[t.id] > this.t - 10)) continue;
        u.cd[0] = u.cd[1] = u.cd[2] = 0;
        if (u.human) this.dmgEvents.push({ x: u.x, y: u.y - 90, text: '熊虎', team: u.team, src: u.id });
      }
    }),
    hook('revive', 20, function (u) { if (u.ztLotus) endLotus(u); }),
  ],
  // 電腦：飛刃先手、刀舞貼身、疾影切入殘血；十二創身邊有敵將才開
  ai: {
    ztKnife: ({ t, d, hero }) => !!t && d < 600 && (hero || d < 300),
    ztLotus: ({ w, u, hero, d }) => hero && d < 300 && w.units.filter((t: Unit) => t.kind === 'hero' && t.alive && t.team === 1 - u.team && gd(u.x, u.y, t.x, t.y) < 450).length >= 1,
  },
});
